import React, { useCallback, useMemo } from 'react';
import { Card, css, format, MAGNETIC_TOKENS } from '@appd/uik';
import { BusinessJourneyData, BusinessJourneyLayout, NodeData } from './model';
import { BusinessJourneyGraph } from './BusinessJourneyGraph';
import { BusinessJourneyPresentationNodeRenderer } from './renderer/BusinessJourneyPresentationNodeRenderer';
import { BusinessJourneyStatisticsCard } from './components/BusinessJourneyStatisticsCard';
import { populateParentId } from './utils';
import { HierarchyPointNode } from 'd3-hierarchy';
import { HealthMapping } from './renderer/utils';

const {
  COLORS: { DEFAULT_BORDER },
  SPACING,
} = MAGNETIC_TOKENS;

type Props = {
  businessJourneyData: BusinessJourneyData;
  layout: BusinessJourneyLayout;
};

export function BusinessJourneyPresentationView({ layout, businessJourneyData }: Props) {
  const { journeyHealth, journeyDuration, conversionRate, criticalMilestones, milestones } = businessJourneyData;

  const nodeData = useMemo(() => populateParentId(milestones), [milestones]);

  const nodeRenderer = useCallback(
    (node: HierarchyPointNode<NodeData>): JSX.Element => (
      <BusinessJourneyPresentationNodeRenderer data={node.data} isRoot={node.depth == 0} />
    ),
    [],
  );

  return (
    <Card
      titleTag={{ type: HealthMapping[journeyHealth].healthType }}
      className={css({ height: '100%', width: '100%', border: 'none', padding: SPACING[6] })}
    >
      <div className={css({ display: 'flex', flexDirection: 'row', gap: SPACING[6] })}>
        <BusinessJourneyStatisticsCard
          className={css({ flex: 'auto' })}
          title="Journey duration"
          value={format.duration(journeyDuration.current)}
        />
        <BusinessJourneyStatisticsCard
          className={css({ flex: 'auto' })}
          title="Overall conversion rate"
          value={format.number.percent(conversionRate.current)}
        />
        <BusinessJourneyStatisticsCard
          className={css({ flex: 'auto' })}
          title="Total critical milestones"
          value={format.number(criticalMilestones.current)}
        />
      </div>
      <hr
        className={css({
          width: '100%',
          borderBottom: `1px solid ${DEFAULT_BORDER}`,
          borderTop: 0,
          borderLeft: 0,
          borderRight: 0,
        })}
      />
      <div className={css({ flex: '1 1 0', overflow: 'auto', paddingLeft: SPACING[6], paddingRight: SPACING[6] })}>
        <BusinessJourneyGraph<NodeData>
          layout={layout}
          nodesData={nodeData}
          nodeRenderer={nodeRenderer}
          nodeHeight={128}
          nodeWidth={420}
        />
      </div>
    </Card>
  );
}
